import { useEffect, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { API } from '@/shared/api';
import { queryClient } from '@/shared/config';
import { getABTest, isABTestEnabled } from '@/shared/lib/abTest';
import { User } from '@/shared/types/User';

import { USER_QUERY_KEYS } from '../lib/userQueryKeys';
import { useOnboardingStore } from './useOnboardingStore';

export const useCurrentUser = <TData = User>(select?: (data: User) => TData) => {
  const updateOnboarding = useOnboardingStore((state) => state.updateOnboardingFromUser);

  const query = useQuery({
    queryFn: API.getUser,
    queryKey: USER_QUERY_KEYS.userData,
    refetchOnMount: false,
    select,
  });

  useEffect(() => {
    if (query.isSuccess) {
      const user = queryClient.getQueryData<undefined | User>(USER_QUERY_KEYS.userData);

      if (user) {
        const demoProjectsTrialABTest = getABTest({
          testName: ['demo_project'],
          tests: user.feature_groups,
        });

        const isTestEnabled = isABTestEnabled(demoProjectsTrialABTest);
        const isDemoView = isTestEnabled && !user.subscription;

        updateOnboarding(user, !isDemoView);
      }
    }
  }, [query.isSuccess, updateOnboarding]);

  return query;
};

export const useIsAuthenticated = () => {
  const query = useCurrentUser();

  return useMemo(() => Boolean(query.isSuccess && query.data), [query.data, query.isSuccess]);
};
